/**
 * Los cobros del médico y la subida del comprobante.
 *
 * El detalle explica **de dónde sale la cifra** — «33,33% de ₡73.450» — porque
 * un monto sin origen es el motivo número uno de una llamada.
 *
 * Lista y detalle son UNA pantalla con rutas anidadas (`cobros` → lista +
 * `<Outlet/>`; `cobros/:id` → detalle): en escritorio se ven lado a lado
 * (master-detail, lista fija a la izquierda), y en el teléfono siguen siendo
 * dos pantallas — la lista, y el detalle a pantalla completa con su flecha de
 * vuelta. Quién se ve y quién no lo decide `portal.css` a partir de la clase
 * `cobros--detalle`, que sale de la ruta. Un solo fetch de lista y uno de
 * detalle, como antes.
 */

import { useState, type DragEvent } from 'react';
import { Link, Outlet, useParams } from 'react-router-dom';
import {
  ALLOCATION_MODE_LABEL, dateLabel, PAYMENT_METHOD_LABEL, periodLabel, today,
  type AllocationMode, type InvoiceStatus, type PaymentMethod, type PaymentStatus,
} from '@cleanblue/shared';
import { ApiError } from '@/lib/api';
import { useApi } from '@/lib/hooks';
import { useQueryClient } from '@tanstack/react-query';
import { Async, Badge, Button, Empty, Field, KeyValueRow, Modal, Money, Note } from '@/components/ui';
import { Icon } from '@/components/common/NavIcons';
import {
  InvoiceViewer, isCreditNote, type InvoiceDetail,
} from '@/components/common/InvoiceDocument';
import { ComoPagarBoton } from './ComoPagar';
import { AnioCobros } from './AnioCobros';

interface ChargeRow {
  id: string; period_start: string; due_minor: number; paid_minor: number;
  due_date: string; payment_status: PaymentStatus; invoice_status: InvoiceStatus;
  unit_code: string; service_name_snapshot: string;
}

export function PortalCharges() {
  /* El `id` del hijo (`:id`) llega al padre: React Router acumula los params
     de toda la rama. Con él se resalta el activo y se decide la vista móvil. */
  const { id } = useParams<{ id: string }>();

  return (
    <div className={`cobros${id ? ' cobros--detalle' : ''}`}>
      <section className="msec cobros__lista">
        {/* «Cómo pagar» vive acá y no en Más (dueño, 8-set-2026): se busca en
            el momento de pagar, y antes había que salir de la pantalla del
            cobro, entrar a Más y bajar hasta el fondo. */}
        <div className="row" style={{ alignItems: 'center' }}>
          <div className="msec__title">Mis cobros</div>
          <span className="spacer" />
          <ComoPagarBoton />
        </div>
        {/* El año completo en vez de la tira plana de meses propios (dueño,
            8-set-2026): «los médicos están acostumbrados a razonar sobre los
            doce meses del año, aunque en un consultorio con rotación solo les
            toque pagar algunos». Lo que se puede ver de un mes ajeno lo decide
            el servidor, no esta pantalla — ver `domain/anio-cobros.ts`. */}
        <AnioCobros />
      </section>

      <div className="cobros__detalle">
        <Outlet />
      </div>
    </div>
  );
}

/** Lo que se ve a la derecha en escritorio cuando no hay cobro elegido. En el
 *  teléfono no existe: ahí la lista ocupa la pantalla (ver portal.css). */
export function PortalChargesIndex() {
  return (
    <div className="cobros__vacio">
      <Empty
        icon="cobros"
        title="Elegí un cobro"
        hint="Acá vas a ver de dónde sale el monto y vas a poder subir el comprobante."
      />
    </div>
  );
}

interface ChargeDetail {
  usual_method: PaymentMethod | null;
  charge: ChargeRow & {
    subtotal_minor: number; discount_minor: number; tax_minor: number;
    share_basis: number | null; share_kind: string | null;
    charge_total_minor: number; allocation_mode_snapshot: AllocationMode;
    period_end: string; unit_name: string | null;
  };
  submissions: {
    id: string; declared_amount_minor: number; declared_method: PaymentMethod;
    status: string; rejection_reason: string | null; created_at: string;
  }[];
  payments: { id: string; amount_minor: number; paid_at: string; method: PaymentMethod; status: string }[];
  /* La factura real emitida por GTI, de `external_invoices`. Antes esto salía
     de `invoice_requests` —la cola interna de facturación, que está vacía
     porque Clean Blue no factura desde la aplicación— así que el cobro decía
     «Facturado» y no había nada que abrir. */
  invoice: {
    id: string; document_number: string; document_type: string | null;
    issued_on: string; total_minor: number;
    pdf_storage_key: string | null; xml_storage_key: string | null;
  } | null;
}

export function PortalChargeDetail() {
  const { id } = useParams<{ id: string }>();
  const query = useApi<ChargeDetail>(`/portal/charges/${id}`);
  const [paying, setPaying] = useState(false);
  const [viendoFactura, setViendoFactura] = useState(false);

  return (
    <>
      {/* La vuelta a la lista. Antes el detalle no tenía cómo volver salvo la
          pestaña de abajo (bug A5). En escritorio se oculta: la lista está al
          lado. */}
      <Link to="/portal/cobros" className="cobros__volver">
        <Icon name="volver" size={18} />
        Mis cobros
      </Link>

      <Async query={query}>
        {({ charge: c, submissions, payments, invoice, usual_method }) => {
          const open = submissions.find((s) => s.status === 'SUBMITTED' || s.status === 'UNDER_REVIEW');
          const rejected = submissions.find((s) => s.status === 'REJECTED');
          const owed = c.due_minor - c.paid_minor;

          return (
            <>
              <section className="msec">
                <div className="mcard mcard--hero">
                  <div className="msec__title" style={{ marginBottom: 4 }}>
                    {periodLabel(c.period_start)}
                  </div>
                  <Money value={owed} size="xl" />
                  <p className="small muted mt-1">
                    {c.unit_code} · {c.service_name_snapshot} · vence el {dateLabel(c.due_date)}
                    {c.due_date < today() && c.payment_status !== 'PAID' && ' · vencido'}
                  </p>

                  {c.payment_status === 'PAID' ? (
                    <Note tone="ok">Este cobro está pagado. Gracias.</Note>
                  ) : open ? (
                    <Note tone="info">
                      Recibimos tu comprobante y lo estamos revisando. Te avisamos apenas se confirme.
                    </Note>
                  ) : (
                    <Button variant="primary" block onClick={() => setPaying(true)}>
                      Subir comprobante de pago
                    </Button>
                  )}

                  {rejected && !open && c.payment_status !== 'PAID' && (
                    <Note tone="warn">
                      El comprobante anterior no se pudo aceptar: {rejected.rejection_reason}
                    </Note>
                  )}
                </div>
              </section>

              <section className="msec">
                <div className="msec__title">De dónde sale este monto</div>
                {/* `.mcard--par`: un par etiqueta/valor a 700px de distancia no
                    se lee como par. */}
                <div className="mcard mcard--par">
                  {/* Explicar la cifra es la diferencia entre pagar y llamar a preguntar. */}
                  <KeyValueRow label="Servicio del mes" style={{ paddingTop: 0 }}><Money value={c.charge_total_minor} /></KeyValueRow>
                  <KeyValueRow label="Reparto">{ALLOCATION_MODE_LABEL[c.allocation_mode_snapshot]}</KeyValueRow>
                  {c.share_kind === 'PERCENT' && c.share_basis != null && (
                    <KeyValueRow label="Tu parte">{c.share_basis}%</KeyValueRow>
                  )}
                  <KeyValueRow label="Subtotal"><Money value={c.subtotal_minor} /></KeyValueRow>
                  {c.discount_minor > 0 && (
                    <KeyValueRow label="Descuento"><Money value={-c.discount_minor} /></KeyValueRow>
                  )}
                  <KeyValueRow label="IVA"><Money value={c.tax_minor} /></KeyValueRow>
                  <KeyValueRow label={<strong>Total</strong>} style={{ borderBottom: 0 }}><Money value={c.due_minor} /></KeyValueRow>
                </div>
              </section>

              {payments.length > 0 && (
                <section className="msec">
                  <div className="msec__title">Pagos registrados</div>
                  <div className="mcard mcard--par">
                    {payments.map((p) => (
                      <KeyValueRow key={p.id} label={<>{dateLabel(p.paid_at.slice(0, 10))} · {PAYMENT_METHOD_LABEL[p.method]}</>}>
                        <Money value={p.amount_minor} />
                        {p.status === 'REVERSED' && <Badge tone="neutral"> reversado</Badge>}
                      </KeyValueRow>
                    ))}
                  </div>
                </section>
              )}

              {invoice && (
                <section className="msec">
                  <div className="msec__title">Factura electrónica</div>
                  <div className="mcard mcard--par">
                    <KeyValueRow label="Comprobante" style={{ paddingTop: 0 }}>
                      <span className="mono small">{invoice.document_number}</span>
                    </KeyValueRow>
                    <KeyValueRow label="Emitida" style={{ borderBottom: 0 }}>
                      {dateLabel(invoice.issued_on)}
                    </KeyValueRow>
                  </div>
                  <div className="row mt-2" style={{ gap: 8 }}>
                    <Button variant="primary" onClick={() => setViendoFactura(true)}>Ver PDF</Button>
                    {invoice.xml_storage_key && (
                      <a
                        className="btn"
                        href={`/api/portal/charges/${c.id}/invoice/xml`}
                        title="El comprobante electrónico legal, para tu contabilidad"
                      >
                        Descargar XML
                      </a>
                    )}
                  </div>
                </section>
              )}

              {viendoFactura && invoice && (
                <FacturaModal
                  chargeId={c.id}
                  numero={invoice.document_number}
                  tipo={invoice.document_type}
                  hasPdf={Boolean(invoice.pdf_storage_key)}
                  onClose={() => setViendoFactura(false)}
                />
              )}

              {paying && (
                <ReceiptModal chargeId={c.id} owed={owed} usualMethod={usual_method} onClose={() => setPaying(false)} />
              )}
            </>
          );
        }}
      </Async>
    </>
  );
}

/**
 * La factura del mes, para el cliente.
 *
 * Es el mismo documento que ve el dueño en `Facturación`, con el mismo render
 * compartido: el papel que este cliente ya recibió por correo alguna vez, y que
 * hasta ahora solo podía volver a pedir por WhatsApp.
 *
 * Las mismas dos formas de llevársela que tiene el dueño, ni una menos: la
 * vista —que se imprime o se guarda como PDF— y el XML, que se baja desde el
 * detalle del cobro y es el comprobante legal ante Hacienda.
 */
function FacturaModal({ chargeId, numero, tipo, hasPdf, onClose }: {
  chargeId: string; numero: string; tipo: string | null; hasPdf: boolean; onClose: () => void;
}) {
  const query = useApi<InvoiceDetail>(`/portal/charges/${chargeId}/invoice/detail`);

  return (
    <Modal
      title={`${isCreditNote(tipo) ? 'Nota de crédito' : 'Factura'} ${numero}`}
      wide
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cerrar</Button>
          {hasPdf ? (
            <a className="btn btn--primary" target="_blank" rel="noreferrer"
              href={`/api/portal/charges/${chargeId}/invoice/pdf`}>Abrir o guardar PDF</a>
          ) : (
            <Button variant="primary" onClick={() => window.print()}>Imprimir o guardar PDF</Button>
          )}
        </>
      }
    >
      <Async query={query}>{(d) => (
        <InvoiceViewer
          documentType={tipo}
          receptorName={null}
          d={d}
          pdfUrl={`/api/portal/charges/${chargeId}/invoice/pdf`}
        />
      )}</Async>
    </Modal>
  );
}

/**
 * Subida del comprobante.
 *
 * Va como `multipart` porque lleva la foto, y el archivo termina en un bucket
 * privado: no existe una URL pública que alguien pueda adivinar.
 */
/* Los métodos que Clean Blue de verdad acepta hoy. El catálogo completo tiene
   además transferencia manual, pago en línea y Apple Pay, pero listarlos acá
   sería ofrecer lo que no existe (pedido del dueño, 29-ago-2026): cuando uno
   se habilite, se agrega a esta lista y nada más. */
const METODOS_DISPONIBLES: PaymentMethod[] = ['SINPE_MOBILE', 'BANK_TRANSFER', 'CASH', 'OTHER'];

/* Lo mismo que el `accept` del input, aplicado a lo que se suelta con el
   mouse: el diálogo de archivos filtra solo, el arrastre no. */
const esComprobante = (f: File) => f.type.startsWith('image/') || f.type === 'application/pdf';

function ReceiptModal({ chargeId, owed, usualMethod, onClose }: {
  chargeId: string; owed: number; usualMethod: PaymentMethod | null; onClose: () => void;
}) {
  const client = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  /* Arrastrando por encima: la zona cambia borde, fondo y texto («Soltala
     acá»), no solo el color. */
  const [arrastrando, setArrastrando] = useState(false);
  /* El monto se escribe EN COLONES, que es el idioma del cliente — pedirle
     «7345000 céntimos» era absurdo (reporte del dueño, 29-ago-2026). Viene
     precargado con lo que debe: en el caso normal no hay que tocarlo. La
     conversión a céntimos, que es como viaja el dinero por la API, pasa al
     enviar. */
  /* Se guardan SOLO los dígitos y se muestra formateado («73,450»): el
     cliente ve plata, no un número pelado (pedido del dueño, 29-ago-2026:
     «siempre pensemos como la experiencia del usuario»). El agrupador de
     miles es el mismo de `formatMoney`, para que el campo hable el mismo
     idioma que el resto del sistema. */
  const [colones, setColones] = useState(String(Math.round(owed / 100)));
  /* El monto viene PROTEGIDO: el sistema ya sabe cuánto debe la persona, y en
     el caso normal no hay nada que escribir — cambiarlo casualmente solo
     produce errores. Se puede corregir (un abono parcial existe), pero con
     una acción explícita, no al alcance de un roce (pedido del dueño,
     29-ago-2026: «sí es bueno que se pueda cambiar, pero no tan fácil»). */
  const [editandoMonto, setEditandoMonto] = useState(false);
  const conMiles = (digitos: string): string =>
    digitos.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  /* Arranca en el método con el que esta persona pagó la última vez: cada
     cliente paga siempre igual. Si nunca pagó (o pagó con uno ya no ofrecido),
     SINPE, que es lo más común. */
  const [method, setMethod] = useState<PaymentMethod>(
    usualMethod && METODOS_DISPONIBLES.includes(usualMethod) ? usualMethod : 'SINPE_MOBILE');
  const [paidOn, setPaidOn] = useState(today());
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /* Lo que el sistema LEYÓ de la captura (fecha, monto, referencia). La
     referencia viaja con el envío sin que el cliente la vea ni la escriba;
     la fecha rellena el campo; el monto solo se informa — el campo va
     protegido y no se pisa solo. */
  const [leido, setLeido] = useState<{ paid_on: string | null; amount_minor: number | null; reference: string | null } | null>(null);
  const [leyendo, setLeyendo] = useState(false);
  /* Distinto de `leido === null`: eso también es el estado inicial, antes de
     intentar nada. Sin esto, una lectura que termina sin sacar nada (imagen
     borrosa, el modelo caído) se veía IGUAL que «todavía no probamos» — el
     cliente nunca se enteraba de que el sistema ya lo intentó y no pudo. */
  const [noLeido, setNoLeido] = useState(false);

  async function analizar(f: File) {
    setLeyendo(true);
    setLeido(null);
    setNoLeido(false);
    try {
      const fd = new FormData();
      fd.set('file', f);
      const r = await fetch('/api/portal/receipt-scan', {
        method: 'POST', body: fd, credentials: 'same-origin',
      });
      if (!r.ok) { setNoLeido(true); return; }
      const d = await r.json() as { paid_on: string | null; amount_minor: number | null; reference: string | null };
      if (d.paid_on || d.amount_minor || d.reference) {
        setLeido(d);
        if (d.paid_on) setPaidOn(d.paid_on);
      } else {
        setNoLeido(true);
      }
    } catch {
      // La lectura es una cortesía, no un paso del flujo — pero el cliente
      // igual merece saber que no funcionó, para completar a mano sin creer
      // que el sistema ya se encargó.
      setNoLeido(true);
    } finally {
      setLeyendo(false);
    }
  }

  /* Un solo camino para el archivo, venga del diálogo o del arrastre. */
  function elegir(f: File | null) {
    setFile(f);
    if (f) {
      setError(null);
      void analizar(f);
    }
  }

  function soltar(e: DragEvent<HTMLLabelElement>) {
    e.preventDefault();
    setArrastrando(false);
    const f = e.dataTransfer.files?.[0] ?? null;
    if (!f) return;
    if (!esComprobante(f)) {
      setError('Solo se acepta una imagen o un PDF.');
      return;
    }
    elegir(f);
  }

  async function submit() {
    if (!file) { setError('Adjuntá la foto del comprobante'); return; }
    const monto = Number(colones) * 100;
    if (!Number.isFinite(monto) || monto <= 0) {
      setError('El monto no se entiende. Escribilo en colones, por ejemplo 73450.');
      return;
    }
    setBusy(true);
    setError(null);

    const form = new FormData();
    form.set('file', file);
    form.set('amount', String(monto));
    form.set('method', method);
    form.set('paid_on', paidOn);
    if (leido?.reference) form.set('reference', leido.reference);
    /* La referencia ya no se le pide al cliente: está en la captura que sube,
       y la lee el administrador al confirmar el pago. Menos campos, menos
       fricción, y el dato queda donde es verificable — en el comprobante. */

    try {
      const response = await fetch(`/api/portal/charges/${chargeId}/receipt`, {
        method: 'POST', body: form, credentials: 'same-origin',
      });
      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new ApiError('upload_failed', body?.error?.message ?? 'No se pudo subir', response.status);
      }
      await client.invalidateQueries();
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'No se pudo subir el comprobante');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title="Subir comprobante"
      onClose={onClose}
      footer={(
        <>
          <Button onClick={onClose}>Cancelar</Button>
          <Button variant="primary" disabled={busy || !file} onClick={() => void submit()}>
            {busy ? 'Enviando…' : 'Enviar comprobante'}
          </Button>
        </>
      )}
    >
      {error && <Note tone="danger">{error}</Note>}

      {/* El comprobante es EL protagonista: la misma zona de soltar del
          resto del sistema, que en el teléfono es un blanco grande para
          tocar y abre la galería — donde vive la captura del SINPE. Sin
          `capture`: forzaba la cámara, y nadie le toma foto a su propia
          pantalla. En escritorio también se puede arrastrar. */}
      <label
        className={`dropzone${arrastrando ? ' is-over' : ''}`}
        style={{ marginBottom: 14 }}
        onDragOver={(e) => { e.preventDefault(); setArrastrando(true); }}
        onDragLeave={() => setArrastrando(false)}
        onDrop={soltar}
      >
        <input
          type="file"
          accept="image/*,application/pdf"
          style={{ display: 'none' }}
          onChange={(e) => elegir(e.target.files?.[0] ?? null)}
        />
        <span className="dropzone__txt">
          {arrastrando ? 'Soltala acá' : file ? file.name : 'Tocá para subir la captura del pago'}
        </span>
        <span className="dropzone__sub">
          {leyendo
            ? 'Leyendo la captura…'
            : leido
              ? `Leímos: ${[
                leido.paid_on ? dateLabel(leido.paid_on) : null,
                leido.reference ? `ref. ${leido.reference}` : null,
                leido.amount_minor ? `₡${Math.round(leido.amount_minor / 100).toLocaleString('en-US')}` : null,
              ].filter(Boolean).join(' · ')}`
              : noLeido
                ? 'No pudimos leerla — completá el monto y la fecha abajo.'
                : file
                  ? 'Lista. Podés cambiarla tocando de nuevo.'
                  : 'La del SINPE o la transferencia. La referencia la vemos ahí.'}
        </span>
      </label>

      <Field label="Monto pagado" required
        hint={editandoMonto
          ? 'Escribí lo que pagaste de verdad.'
          : 'Es lo que debés este mes. Tocá «Corregir» solo si pagaste otra cosa.'}>
        {editandoMonto ? (
          /* `text` y no `number`: un input numérico no admite el separador
             de miles, y acá el formato ES el punto. Solo entran dígitos; el
             símbolo vive ADENTRO del marco, como parte del campo. */
          <div className="input money-field">
            <span className="money-field__simbolo" aria-hidden="true">₡</span>
            <input
              className="money-field__inner"
              type="text"
              inputMode="numeric"
              autoComplete="off"
              autoFocus
              value={conMiles(colones)}
              onChange={(e) => setColones(e.target.value.replace(/\D/g, '').slice(0, 9))}
            />
          </div>
        ) : (
          <div className="input money-field money-field--fijo">
            <span className="money-field__simbolo" aria-hidden="true">₡</span>
            <span className="money-field__inner">{conMiles(colones)}</span>
            <button
              type="button"
              className="money-field__corregir"
              onClick={() => setEditandoMonto(true)}
            >
              Corregir
            </button>
          </div>
        )}
      </Field>

      <Field label="Cómo pagaste" required>
        <select className="select" value={method} onChange={(e) => setMethod(e.target.value as PaymentMethod)}>
          {METODOS_DISPONIBLES.map((m) => (
            <option key={m} value={m}>{PAYMENT_METHOD_LABEL[m]}</option>
          ))}
        </select>
      </Field>

      <Field label="Fecha del pago" required>
        <input className="input" type="date" value={paidOn} onChange={(e) => setPaidOn(e.target.value)} />
      </Field>
    </Modal>
  );
}
